// src/main.js
import {createApp} from 'vue';
import {createI18n} from 'vue-i18n';
import App from './App.vue';
import router from './router';
import {createPinia} from 'pinia';
import keycloak from "./keycloak";
import { useUserStore } from "@/stores/userStore";
import { rememberLoginPath, consumeLoginPath } from "@/utils/authNavigation.cjs";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { useCompanyFeatureStore } from "@/stores/companyFeatureStore";
import "@/themes/themes.css";

// VCalendar

import { setupCalendar, Calendar, DatePicker } from 'v-calendar';
import 'v-calendar/style.css';

// Bootstrap CSS and JavaScript dependencies
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js'; // Includes Popper.js
import {de, en} from './i18n/messages.js';

const i18n = createI18n({
    locale: 'de',
    fallbackLocale: 'en',
    messages: {
        en,
        de,
    }
})


const app = createApp(App);
const pinia = createPinia()

// Use plugin defaults (optional)
app.use(setupCalendar, {})

// Use the components
app.component('VCalendar', Calendar)
app.component('VDatePicker', DatePicker)

app.use(pinia)
app.use(router);
app.use(i18n);

const disableAuthForE2E = process.env.VUE_APP_DISABLE_AUTH === "true";
const userStore = useUserStore(pinia);
window.addEventListener("administrative-mfa-required", () => { router.replace({ name: "AdministrativeMfa" }); });
window.addEventListener("company-selection-invalid", () => {
    userStore.me = null;
    userStore.accessState = "CompanySelectionInvalid";
    router.replace({ name: "AssignmentPending" });
});
const entitlementStore = useEntitlementStore(pinia);
const companyFeatureStore = useCompanyFeatureStore(pinia);
window.addEventListener("company-feature-disabled", async () => {
    try {
        await companyFeatureStore.loadFeatures();
    } catch (_) {
        companyFeatureStore.clear();
    }
    const activeFeature = router.currentRoute.value.meta?.companyFeature;
    if (activeFeature && !companyFeatureStore.isEnabled(activeFeature)) {
        router.replace({ name: "Forbidden" });
    }
});
let isMounted = false;

function mountAppOnce() {
    if (isMounted) {
        return;
    }

    app.mount("#app");
    isMounted = true;
}

async function showAuthFallback(error) {
    console.error("Authentication Failed", error);
    mountAppOnce();
    try {
        await router.replace({ name: "AccessUnavailable" });
    } catch (navigationError) {
        console.error("Failed to navigate to Forbidden route after auth failure:", navigationError);
    }
}

router.beforeEach(async (to) => {
    if (disableAuthForE2E) {
        return true;
    }

    // Avoid calling protected API endpoints before Keycloak is initialized.
    // Otherwise axios may send a malformed "Bearer undefined" header and trigger auth loops.
    if (!keycloak.authenticated) {
        return true;
    }

    if (["Forbidden", "AssignmentPending", "AccessSuspended", "AccessUnavailable", "AdministrativeMfa"].includes(to.name)) {
        return true;
    }

    if (userStore.accessState === "CompanySelectionInvalid") {
        return { name: "AssignmentPending" };
    }

    const requiredPermissions = Array.isArray(to.meta?.permissions)
        ? to.meta.permissions
        : to.meta?.permission
            ? [to.meta.permission]
            : [];
    const requiredPermissionsAny = to.meta?.permissionsAny || [];
    const requiredModules = to.meta?.modules || [];
    const requiredModulesAny = to.meta?.modulesAny || [];
    const requiredCapabilitiesAny = to.meta?.capabilitiesAny || [];
    const requiredCompanyFeature = to.meta?.companyFeature;
    if (!requiredPermissions.length && !requiredPermissionsAny.length
        && !requiredModules.length && !requiredModulesAny.length && !requiredCapabilitiesAny.length && !requiredCompanyFeature) {
        return true;
    }

    if (!userStore.me) {
        try {
            await userStore.loadMe();
        } catch (error) {
            console.error("Failed to load user session for route authorization:", error);
            return { name: userStore.accessState === "Suspended" ? "AccessSuspended"
                : userStore.isAwaitingAssignment ? "AssignmentPending" : "AccessUnavailable" };
        }
    }

    if (userStore.isAwaitingAssignment) {
        return { name: "AssignmentPending" };
    }

    if (!requiredPermissions.every((permission) => userStore.hasPermission(permission))) {
        return { name: "Forbidden" };
    }

    if (
        requiredPermissionsAny.length > 0 &&
        !requiredPermissionsAny.some((permission) => userStore.hasPermission(permission))
    ) {
        return { name: "Forbidden" };
    }

    if (requiredModules.length > 0 || requiredModulesAny.length > 0 || requiredCapabilitiesAny.length > 0) {
        if (!entitlementStore.isLoaded) {
            try {
                await entitlementStore.loadCapabilities();
            } catch (error) {
                console.error("Failed to load module capabilities for route authorization:", error);
                return { name: "Forbidden" };
            }
        }

        if (!entitlementStore.hasAllModules(requiredModules)) {
            return { name: "Forbidden" };
        }

        if (!entitlementStore.hasAnyModules(requiredModulesAny)) {
            return { name: "Forbidden" };
        }
        if (requiredCapabilitiesAny.length > 0 && !requiredCapabilitiesAny.some(({ module, permission }) =>
            entitlementStore.hasModule(module) && userStore.hasPermission(permission))) {
            return { name: "Forbidden" };
        }
    }

    if (requiredCompanyFeature) {
        if (!companyFeatureStore.loaded || companyFeatureStore.companyId !== userStore.currentCompanyId) {
            try {
                await companyFeatureStore.loadFeatures();
            } catch (error) {
                console.error("Failed to load company features:", error);
                return { name: "Forbidden" };
            }
        }
        if (!companyFeatureStore.isEnabled(requiredCompanyFeature)) return { name: "Forbidden" };
    }

    return true;
});

// Apply persisted UI settings early (theme + language), even before we can load /user/me.
userStore.applyLocaleToI18n(i18n);
userStore.applyTheme();

if (disableAuthForE2E) {
    mountAppOnce();
} else {
    const keycloakRedirectUri = `${window.location.origin}/`;
    try {
        rememberLoginPath(window.sessionStorage, window.location.pathname);
    } catch {
        // Authentication remains available when browser storage is disabled.
    }

    keycloak
        .init({
            onLoad: "login-required",
            redirectUri: keycloakRedirectUri,
            pkceMethod: "S256",
        })
        .then(async (authenticated) => {
            if (!authenticated) {
                // Avoid white-screen/reload loops. Force login once, otherwise show a visible fallback page.
                try {
                    await keycloak.login({ redirectUri: keycloakRedirectUri });
                    return;
                } catch (loginError) {
                    await showAuthFallback(loginError);
                    return;
                }
            } else {
                let returnPath = null;
                try {
                    returnPath = consumeLoginPath(window.sessionStorage);
                } catch {
                    // Continue on the callback route when storage is unavailable.
                }
                setInterval(() => {
                    keycloak.updateToken(30).catch(() => {
                        console.error("Failed to refresh token");
                    });
                }, 60000);

                try {
                    await userStore.loadMe();
                } catch (e) {
                    await router.replace({ name: userStore.accessState === "Suspended" ? "AccessSuspended"
                        : userStore.isAwaitingAssignment ? "AssignmentPending" : "AccessUnavailable" });
                    mountAppOnce();
                    return;
                }
                try {
                    await entitlementStore.loadCapabilities();
                } catch (capabilityError) {
                    console.error("Failed to load module capabilities:", capabilityError);
                }
                userStore.applyLocaleToI18n(i18n);
                userStore.applyTheme();

                if (userStore.isAwaitingAssignment) {
                    await router.replace({ name: "AssignmentPending" });
                } else if (returnPath) {
                    await router.replace(returnPath);
                } else {
                    const requiredModules = router.currentRoute.value.meta?.modules || [];
                    const requiredModulesAny = router.currentRoute.value.meta?.modulesAny || [];
                    if ((requiredModules.length > 0
                            && !entitlementStore.hasAllModules(requiredModules))
                        || (requiredModulesAny.length > 0
                            && !entitlementStore.hasAnyModules(requiredModulesAny))) {
                        await router.replace({ name: "Forbidden" });
                    }
                }

                mountAppOnce();
            }
        })
        .catch(async (e) => {
            await showAuthFallback(e);
        });
}

